import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Reason } from '../services/types';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import { useAppState } from '../state/AppState';
import { useDataTransformation } from '../hooks/useDataTransformation';
import { navigateToNextScreenFrom } from '../utils/smartNavigation';

/**
 * ScreenJ - Reason selection screen.
 *
 * Allows the user to select reasons for their visit from a list.
 */
const ScreenJ: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch, state } = useAppState();
  const { specialties, hasAvailabilityData } = useDataTransformation();
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);

  // Convert specialties to Reason format and sort alphabetically
  const reasons: Reason[] = specialties.map((specialty) => ({
    id: specialty,
    name: specialty
  })).sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    const loadReasons = async () => {
      try {
        if (hasAvailabilityData && specialties.length > 0) {
          setLoading(false);
          return;
        }
        
        // Fallback to static data if no availability data
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    // Add a small delay to ensure availability data is loaded
    const timeoutId = setTimeout(loadReasons, 500);
    
    return () => clearTimeout(timeoutId);
  }, [hasAvailabilityData, specialties.length]);

  // Initialize selected reasons from app state
  useEffect(() => {
    if (state.reason && reasons.length > 0) {
      // Split the reason string by comma and trim whitespace
      const selectedReasons = state.reason.split(',').map(r => r.trim()).filter(r => r.length > 0);
      setSelected(selectedReasons);
    }
  }, [state.reason, reasons.length]);

  const handleToggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleContinue = () => {
    dispatch({ type: 'SET_REASON', payload: selected.join(', ') });
    
    // Use smart navigation to determine next screen from Screen J
    navigateToNextScreenFrom(state, 'j', navigate);
  };

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card">
            <h2 className="mtg-screen-question mb-18" id="reasons-title">
              Reasons for<br />your visit.
            </h2>
            <div style={{ width: '100%', margin: '0 auto 18px auto', maxWidth: 280 }}>
              <fieldset>
                <legend className="sr-only">What brings you in today</legend>
                <div 
                  className="mtg-scrollable-list"
                  role="group"
                  aria-labelledby="reasons-title"
                  aria-describedby="reasons-description"
                >
                  <div id="reasons-description" className="sr-only">
                    Select all reasons that apply to your visit. You can select multiple options.
                  </div>
                  {loading ? (
                    <div style={{ color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">Loading...</div>
                  ) : (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {reasons.map((reason) => (
                        <li key={reason.id} className="mb-6 d-flex align-center">
                          <input
                            type="checkbox"
                            checked={selected.includes(reason.id)}
                            onChange={() => handleToggle(reason.id)}
                            style={{ 
                              marginRight: 10, 
                              width: 18, 
                              height: 18,
                              minWidth: 18,
                              minHeight: 18,
                              maxWidth: 18,
                              maxHeight: 18,
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                              border: '0.5px solid #e69e6d',
                              borderRadius: '2px',
                              backgroundColor: selected.includes(reason.id) ? '#e69e6d' : 'transparent',
                              cursor: 'pointer',
                              position: 'relative',
                              boxSizing: 'border-box',
                              flexShrink: 0,
                              padding: 0,
                              margin: '0 10px 0 0',
                              outline: 'none',
                              verticalAlign: 'top'
                            }}
                            id={`reason-${reason.id}`}
                            aria-describedby={`reason-${reason.id}-status`}
                          />
                          <label
                            htmlFor={`reason-${reason.id}`}
                            style={{
                              fontWeight: selected.includes(reason.id) ? 700 : 400,
                              color: selected.includes(reason.id) ? '#1a355e' : '#222',
                              fontStyle: selected.includes(reason.id) ? 'italic' : 'normal',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              paddingTop: "7px",
                              lineHeight: 1
                            }}
                          >
                            {reason.name}
                          </label>
                          <span id={`reason-${reason.id}-status`} className="sr-only" role="status" aria-live="polite">
                            {selected.includes(reason.id) ? `${reason.name} selected` : `${reason.name} not selected`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </fieldset>
              <div style={{ padding: '0 12px' }}>
                <button
                  className="pill-btn-standard mb-18"
                  onClick={handleContinue}
                  disabled={selected.length === 0}
                  aria-describedby="continue-description"
                >
                  Continue
                </button>
                <div id="continue-description" className="sr-only">
                  {selected.length === 0 ? 'Please select at least one reason to continue' : `Continue with ${selected.length} selected reason${selected.length === 1 ? '' : 's'}`}
                </div>
              </div>
            </div>
            <ProgressBar progress={54} />
          </div>
          <button
            className="mtg-secondary-action-btn mb-18"
            onClick={() => navigate('/v')}
            aria-label="My option is not listed, contact us"
          >
            MY OPTION IS NOT LISTED
          </button>
        </main>
      </div>
    </div>
  );
};

export default ScreenJ; 